import { makeObservable, observable, computed, action } from 'mobx';
import isEmpty from 'lodash/isEmpty';
import FilterState from '@features/categories/mobx/state/FilterState';
import { TFilterDetail, TFilterSelectOptions } from '@features/categories/typescript';
import { replaceBrowserSearchParams } from '@utils/replaceBrowserSearchParams';

export default class FilterBooleanState extends FilterState {
  state: Record<string, boolean> = {};

  options: TFilterSelectOptions = {} as TFilterSelectOptions;

  constructor(filter: TFilterDetail) {
    super(filter);
    makeObservable(this, {
      state: observable,
      queryURLString: computed,
      queryNameArray: computed,
      setValue: action,
      resetValue: action,
      value: computed,
      isSet: computed,
    });

    if (filter.options) {
      this.setFilterOptions(filter.options as boolean[]);
    }

    this.state = {};
  }

  get value(): string[] {
    const value: string[] = [];
    Object.keys(this.state).forEach((name) => value.push(name));
    return value;
  }

  get isSet() {
    return !isEmpty(this.state);
  }

  setFilterOptions(options: boolean[]) {
    const newOptions: TFilterSelectOptions = {} as TFilterSelectOptions;

    if (options.includes(true)) {
      newOptions.true = {
        name: 'true',
        featureValueDescriptive: 'yes',
      };
    }

    if (options.includes(false)) {
      newOptions.false = {
        name: 'false',
        featureValueDescriptive: 'no',
      };
    }

    this.options = newOptions;
  }

  setValue(value: string[], noURLUpdate = false) {
    this.state = {};
    value.forEach((name) => {
      this.state[name] = true;
    });
    if (!noURLUpdate) {
      this.markUserActivity();
      this.setValueToURLString();
    }
  }

  resetValue(noURLUpdate = false) {
    this.setValue([], noURLUpdate);
  }

  setValueFromURLString() {
    const urlParams = new URLSearchParams(window.location.search);
    const paramValue = urlParams.get(this.queryNameArray);
    if (paramValue) {
      this.state = {};
      paramValue.split(',').forEach((name) => {
        this.state[name] = true;
      });
    }
  }

  removeValueFromURLString() {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete(this.queryNameArray);
    replaceBrowserSearchParams(urlParams);
  }

  setValueToURLString() {
    if (isEmpty(this.value)) {
      this.removeValueFromURLString();
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set(this.queryNameArray, this.value.join(','));
    replaceBrowserSearchParams(urlParams);
  }

  get queryNameArray() {
    return `${this.queryName}[]`;
  }

  get queryURLString() {
    const urlParams = new URLSearchParams();
    urlParams.append(this.queryNameArray, this.value.toString());

    return decodeURI(urlParams.toString());
  }
}
